import { useState, useCallback, useEffect, useRef } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { authApi, mapAuthError } from "@/lib/api/auth";
import { getPostLoginRedirectPath } from "@/lib/authRedirect";
import { otpStore } from "@/lib/otpStore";
import type { AuthUser as ContextAuthUser } from "@/context/AuthContext";
import type { AuthUser as ApiAuthUser } from "@chinese-vendor/schemas";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;

function toContextUser(apiUser: ApiAuthUser): ContextAuthUser {
  return {
    id: apiUser.id,
    email: apiUser.email,
    firstName: apiUser.first_name,
    lastName: apiUser.last_name,
    roles: apiUser.roles,
    vendorStatus: apiUser.vendor_status,
    applicationId: apiUser.application_id ?? null,
    temporaryPasswordSet: apiUser.temporary_password_set ?? false,
  };
}

export function useOTPFlow() {
  const { login } = useAuth();
  const router = useRouter();
  const params = useLocalSearchParams<{ email?: string }>();
  const email = params.email ?? "";
  const [otpToken, setOtpToken] = useState(() => otpStore.get() ?? "");

  const [code, setCode] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = useCallback(() => {
    setCountdown(RESEND_COOLDOWN_SECONDS);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    otpStore.clear();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const verifyMutation = useMutation({
    mutationFn: ({ otp }: { otp: string }) =>
      authApi.loginWithOTP({ otp_token: otpToken, otp_code: otp }),
    onSuccess: async (result) => {
      await login(result.token, toContextUser(result.user));
      router.replace(
        getPostLoginRedirectPath(
          result.user.roles,
          result.user.vendor_status,
          result.user.temporary_password_set
        )
      );
    },
    onError: () => {
      setCode("");
    },
  });

  const resendMutation = useMutation({
    mutationFn: () => authApi.requestOTP({ email }),
    onSuccess: (data) => {
      setOtpToken(data.otp_token);
      setCode("");
      startCooldown();
    },
  });

  const { isError: verifyIsError, reset: verifyReset } = verifyMutation;

  const handleCodeChange = useCallback(
    (value: string) => {
      setCode(value);
      if (localError) setLocalError(null);
      if (verifyIsError) verifyReset();
    },
    [localError, verifyIsError, verifyReset]
  );

  const handleVerify = useCallback(async () => {
    if (code.length < OTP_LENGTH) return;
    if (!email || !otpToken) {
      setLocalError("auth.otp.error.invalid");
      return;
    }
    setLocalError(null);
    await verifyMutation.mutateAsync({ otp: code }).catch(() => {});
  }, [code, email, otpToken, verifyMutation.mutateAsync]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResend = useCallback(async () => {
    if (countdown > 0 || resendMutation.isPending) return;
    if (!email) return;
    await resendMutation.mutateAsync().catch(() => {});
  }, [countdown, resendMutation.isPending, resendMutation.mutateAsync, email]); // eslint-disable-line react-hooks/exhaustive-deps

  const rawVerifyError = verifyMutation.isError ? verifyMutation.error : null;
  const rawResendError = resendMutation.isError ? resendMutation.error : null;

  const verifyErrorKey = rawVerifyError
    ? (() => {
        const key = mapAuthError(rawVerifyError);
        return key === "auth.login.error.expiredOtp"
          ? "auth.otp.error.expired"
          : "auth.otp.error.invalid";
      })()
    : null;

  const error: string | null =
    localError ?? verifyErrorKey ?? (rawResendError ? mapAuthError(rawResendError) : null);

  const canResend = countdown === 0 && !resendMutation.isPending;
  const isComplete = code.length === OTP_LENGTH;

  return {
    code,
    email,
    isVerifying: verifyMutation.isPending,
    isResending: resendMutation.isPending,
    error,
    countdown,
    canResend,
    isComplete,
    handleCodeChange,
    handleVerify,
    handleResend,
    router,
  };
}
